import clsx from 'clsx';
import { usePathname, useRouter } from 'next/navigation';
import numbro from 'numbro';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CgHeart, CgWebsite } from 'react-icons/cg';
import { FaRetweet } from 'react-icons/fa';
import { FiMoreVertical } from 'react-icons/fi';
import { IoIosArrowRoundBack } from 'react-icons/io';
import { MdVerified } from 'react-icons/md';
import { TiDelete } from 'react-icons/ti';

import { isBlank, isFunction, isNullOrFalse } from 'shared/lib/utils';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Slider } from 'components/ui/slider';
import { Switch } from 'components/ui/switch';

import { StateContext } from '@/lib/StateProvider';
import { graphqlClient, requestGraphql } from '@/lib/backend';
import { formatRelativeFromNow } from '@/lib/datetime';
import { trackClick } from '@/lib/event_tracker';
import {
  biggerProfileImageUrl,
  formatTweetDate,
  parseTweetDate,
} from '@/lib/twitter';
import updateAnnotation from '@/lib/updateAnnotation';
import updateAnnotationSet from '@/lib/updateAnnotationSet';

import { isUntouchedForkedAnnotation } from '@/lib/data/annotation';
import { i18n } from '@/lib/data/i18n';
import { isNew } from '@/lib/data/utils';

import DeleteAnnotationMutation from '@/lib/queries/DeleteAnnotationMutation';

import AnnotationDateAndValues from '@/components/AnnotationDateAndValues';
import EditableField from '@/components/EditableField';
import FormFieldColorPicker from '@/components/FormFieldColorPicker';
import UserPictureAndUsername from '@/components/UserPictureAndUsername';

type AnnotationUrlFormInputs = {
  url: string;
};

function makeAnnotationEventComponent(story, annotation) {
  let annotationEventComponent;
  const annotationEvent = annotation.get('event');

  if (annotation.getIn(['source', 'type']) === 'tweet') {
    annotationEventComponent = (
      <div className="flex items-center gap-4">
        <img
          className="max-h-[2rem] max-w-[2rem] rounded-full sm:max-h-[8rem] sm:max-w-[8rem] md:max-h-[12rem] md:max-w-[12rem] lg:max-h-[16rem] lg:max-w-[16rem]"
          src={biggerProfileImageUrl(
            annotationEvent.getIn(['user', 'profileImageUrl'])
          )}
          alt={annotationEvent.getIn(['user', 'screenName'])}
          loading="lazy"
        />

        <div className="flex flex-col gap-2">
          {/* User details and tweet created at + tweet text. */}
          <div className="flex flex-col gap-1">
            {/* User details and tweet created at. */}
            <p className="flex sm:items-center">
              <span className="flex flex-col sm:flex-row sm:items-center sm:gap-1">
                <span className="flex items-center gap-1 font-semibold">
                  <span className="">
                    {annotationEvent.getIn(
                      ['user', 'name'],
                      annotationEvent.getIn(['user', 'screenName'])
                    )}
                  </span>

                  {annotationEvent.getIn(['user', 'verified']) && (
                    <MdVerified className="inline-block fill-sky-400" />
                  )}
                </span>

                <span className="block text-gray-500">
                  @{annotationEvent.getIn(['user', 'screenName'])}
                </span>
              </span>

              <span className="mx-1 text-gray-400">·</span>

              <span className="text-gray-500">
                <a
                  href={annotation.getIn(['source', 'reference'])}
                  rel="noreferrer"
                  target="_blank"
                >
                  {formatTweetDate(
                    parseTweetDate(annotationEvent.get('createdAt'))
                  )}
                </a>
              </span>
            </p>

            {/* Tweet text. */}
            <p>
              {annotationEvent.get('fullText') || annotationEvent.get('text')}
            </p>
          </div>

          {/* Retweets and likes. */}
          <div className="flex gap-3 text-gray-500">
            <span className="flex items-center gap-1" aria-label="retweet">
              <FaRetweet />

              <span>
                {numbro(annotationEvent.get('retweetCount')).format({
                  average: true,
                  optionalMantissa: true,
                  mantissa: 1,
                })}
              </span>
            </span>

            <span className="flex items-center gap-1" aria-label="like">
              <CgHeart />

              <span>
                {numbro(
                  annotationEvent.get('favoriteCount') ||
                    (annotationEvent.get('retweetedStatus') &&
                      annotationEvent.getIn([
                        'retweetedStatus',
                        'favoriteCount',
                      ]))
                ).format({
                  average: true,
                  optionalMantissa: true,
                  mantissa: 1,
                })}
              </span>
            </span>
          </div>
        </div>
      </div>
    );
  } else if (annotation.getIn(['source', 'type']) === 'website') {
    annotationEventComponent = (
      <div className="flex items-center gap-4">
        {(() => {
          if (isBlank(annotation.getIn(['event', 'image']))) {
            return (
              <CgWebsite size="8rem" className="-mt-[0.75rem]" color="#444" />
            );
          }

          return (
            <img
              className="max-h-[2rem] max-w-[2rem] sm:max-h-[8rem] sm:max-w-[8rem] md:max-h-[12rem] md:max-w-[12rem] lg:max-h-[16rem] lg:max-w-[16rem]"
              src={annotation.getIn(['event', 'image'])}
              alt="Source"
              loading="lazy"
            />
          );
        })()}

        <div className="self-start">
          <h3 className="font-semibold">
            {annotation.getIn(['event', 'title'])}
          </h3>

          <p className="">{annotation.getIn(['event', 'description'])}</p>

          <p className="text-gray-500">
            {i18n(story.get('language'), 'source')}:{' '}
            <a
              href={annotation.getIn(['source', 'reference'])}
              rel="noreferrer"
              target="_blank"
            >
              {annotation.getIn(['source', 'reference'])}
            </a>
          </p>
        </div>
      </div>
    );
  } else if (annotation.getIn(['source', 'type']) === 'self') {
    return null;
  } else {
    console.error(
      `Don't know how to make event component for annotation: ${annotation}`
    );
    return null;
  }

  return annotationEventComponent;
}

const COLORS = [
  '#ffc8c0',
  '#fecf49',
  '#9fe5a2',
  '#b1dbef',
  '#d0d2f7',
  '#d0d5dc',
];

type AnnotationStyleFormInputs = {
  colorfulBackground: boolean;
  paddingHorizontal: number;
  paddingVertical: number;
  fontSize: number;
  length: number;
  textColor: string;
  showOrderIndicators: boolean;
  showTimestamp: boolean;
  showArrow: boolean;
  showLink: boolean;
};

export function Annotation({
  className,
  session,
  annotation,
  setAnnotation,
  onAnnotationFieldChange,
  annotationsRenderer,
  inEditMode,
  setPreventPopoupHide,
  story,
}) {
  const router = useRouter();
  const { dispatch } = useContext(StateContext);
  const pathname = usePathname();
  const graphql = graphqlClient(session);

  const [isEditingColor, setIsEditingColor] = useState(false);

  useEffect(() => {
    if (!isFunction(setPreventPopoupHide)) {
      return () => {
        // Do nothing.
      };
    }

    setPreventPopoupHide(isEditingColor);
  }, [isEditingColor]);

  const annotationStyleForm = useForm<AnnotationStyleFormInputs>();
  const annotationUrlForm = useForm<AnnotationUrlFormInputs>();

  const colorfulBackgroundWatcher = annotationStyleForm.watch(
    'colorfulBackground',
    false
  );

  const annotationToRender = isUntouchedForkedAnnotation(annotation)
    ? annotation.get('createdFrom')
    : annotation;

  async function deleteAnnotation(story, annotation) {
    const { data: _data, error } = await requestGraphql(
      session,
      graphql,
      pathname,
      DeleteAnnotationMutation,
      'deleteAnnotation',
      {
        story_id: story.get('id'),
        annotation_id: annotation.get('id'),
      },
      'Annotation Deletion',
      {
        storyId: story.get('id'),
        storySlug: story.get('slug'),
        annotationId: annotation.get('id'),
      }
    );

    if (error) {
      dispatch({
        type: 'setFlashMessage',
        data: {
          type: 'error',
          message: `${error.errorType}: ${error.errorMessage}`,
        },
      });

      return;
    }

    router.refresh();
  }

  function makeOnBlurHandler(annotationField) {
    if (isNew(annotation)) {
      return (_, _newValue) => {
        // Do nothing.
      };
    }

    return (_e, newValue) => {
      if (isFunction(setPreventPopoupHide)) {
        setPreventPopoupHide(false);
      }

      updateAnnotation(
        session,
        graphql,
        pathname,
        dispatch,
        router,
        annotationsRenderer,
        story,
        annotation,
        {
          [annotationField]: newValue,
        },
        {
          shouldRefreshRouter: true,
        }
      );
    };
  }

  function deleteAnnotationHandler(_e) {
    trackClick(session, pathname, 'Delete Annotation Button', {
      storyId: story.get('id'),
      annotationId: annotation.get('id'),
    });

    if (
      window.confirm(
        `Are you sure you want to delete this ${annotation.getIn([
          'source',
          'type',
        ])} annotation from the story?\n\nYou'll have to add it again if you change your mind.`
      )
    ) {
      trackClick(session, pathname, 'Delete Annotation Confirmation', {
        yes: true,
        storyId: story.get('id'),
        annotationId: annotation.get('id'),
      });

      deleteAnnotation(story, annotation);
    } else {
      trackClick(session, pathname, 'Delete Annotation Confirmation', {
        no: true,
        storyId: story.get('id'),
        annotationId: annotation.get('id'),
      });
    }
  }

  function deleteAnnotationEventHandler(_e) {
    trackClick(session, pathname, 'Delete Annotation Event Button', {
      storyId: story.get('id'),
      annotationId: annotation.get('id'),
    });

    updateAnnotation(
      session,
      graphql,
      pathname,
      dispatch,
      router,
      annotationsRenderer,
      story,
      annotation,
      {
        url: null,
      },
      {
        skipRedraw: false,
        shouldRefreshRouter: true,
      }
    );
  }

  async function addAnnotationEvent({ url }) {
    trackClick(session, pathname, 'Add Annotation Event Button', {
      storyId: story.get('id'),
      annotationId: annotation.get('id'),
    });

    await updateAnnotation(
      session,
      graphql,
      pathname,
      dispatch,
      router,
      annotationsRenderer,
      story,
      annotation,
      {
        url: url,
      },
      {
        skipRedraw: false,
        shouldRefreshRouter: true,
      }
    );
  }

  return (
    <div
      className={clsx([
        // NOTE: "p-4" is synced to event component.
        'flex flex-col gap-4 p-2 !text-[8px] ![line-height:10px] sm:p-4 sm:!text-base',
        className,
      ])}
    >
      {(() => {
        if (isEditingColor) {
          return (
            <Form {...annotationStyleForm}>
              <div className="grid grid-cols-12 gap-8">
                <div className="col-span-5">
                  <form className="flex flex-col gap-8">
                    <FormField
                      control={annotationStyleForm.control}
                      name="showTimestamp"
                      defaultValue={annotation.getIn([
                        'style',
                        'showTimestamp',
                      ])}
                      render={({ field }) => {
                        return (
                          <FormItem className="grid grid-cols-2 items-center gap-2">
                            <FormLabel className="col-span-2 text-xs sm:text-base">
                              Show timestamp?
                            </FormLabel>

                            <FormControl className="col-span-2">
                              <Switch
                                className="!my-0"
                                checked={field.value}
                                onCheckedChange={(isChecked) => {
                                  field.onChange(isChecked);

                                  updateAnnotation(
                                    session,
                                    graphql,
                                    pathname,
                                    dispatch,
                                    router,
                                    annotationsRenderer,
                                    story,
                                    annotation,
                                    {
                                      style_show_timestamp: isChecked,
                                    },
                                    {
                                      shouldRefreshRouter: false,
                                    }
                                  );
                                }}
                              />
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={annotationStyleForm.control}
                      name="showArrow"
                      defaultValue={annotation.getIn(['style', 'showArrow'])}
                      render={({ field }) => {
                        return (
                          <FormItem className="grid grid-cols-2 items-center gap-2">
                            <FormLabel className="col-span-2 text-xs sm:text-base">
                              Show arrow?
                            </FormLabel>

                            <FormControl className="col-span-2">
                              <Switch
                                className="!my-0"
                                checked={field.value}
                                onCheckedChange={(isChecked) => {
                                  field.onChange(isChecked);

                                  updateAnnotation(
                                    session,
                                    graphql,
                                    pathname,
                                    dispatch,
                                    router,
                                    annotationsRenderer,
                                    story,
                                    annotation,
                                    {
                                      style_show_arrow: isChecked,
                                    },
                                    {
                                      shouldRefreshRouter: true,
                                    }
                                  );
                                }}
                              />
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={annotationStyleForm.control}
                      name="showLink"
                      defaultValue={annotation.getIn(['style', 'showLink'])}
                      render={({ field }) => {
                        return (
                          <FormItem className="grid grid-cols-2 items-center gap-2">
                            <FormLabel className="col-span-2 text-xs sm:text-base">
                              Show link?
                            </FormLabel>

                            <FormControl className="col-span-2">
                              <Switch
                                className="!my-0"
                                checked={field.value}
                                onCheckedChange={(isChecked) => {
                                  field.onChange(isChecked);

                                  updateAnnotation(
                                    session,
                                    graphql,
                                    pathname,
                                    dispatch,
                                    router,
                                    annotationsRenderer,
                                    story,
                                    annotation,
                                    {
                                      style_show_link: isChecked,
                                    },
                                    {
                                      shouldRefreshRouter: true,
                                    }
                                  );
                                }}
                              />
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={annotationStyleForm.control}
                      name="showOrderIndicators"
                      defaultValue={story.getIn([
                        'annotationSet',
                        'style',
                        'showOrderIndicators',
                      ])}
                      render={({ field }) => {
                        return (
                          <FormItem className="grid grid-cols-2 items-center gap-2">
                            <FormLabel className="col-span-2 text-xs sm:text-base">
                              Show order indicators?
                            </FormLabel>

                            <FormControl className="col-span-2">
                              <Switch
                                className="!my-0"
                                checked={field.value}
                                onCheckedChange={(isChecked) => {
                                  field.onChange(isChecked);

                                  updateAnnotationSet(
                                    session,
                                    graphql,
                                    pathname,
                                    dispatch,
                                    router,
                                    story,
                                    {
                                      style_show_order_indicators: isChecked,
                                    },
                                    {
                                      shouldRefreshRouter: true,
                                    }
                                  );
                                }}
                              />
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={annotationStyleForm.control}
                      name="fontSize"
                      defaultValue={annotation.getIn(['style', 'fontSize'])}
                      render={({ field }) => {
                        return (
                          <FormItem className="grid grid-cols-2 items-center">
                            <div className="col-span-2 grid grid-cols-2 items-center">
                              <FormLabel className="col-span-1 text-xs sm:text-base">
                                Font size
                              </FormLabel>

                              <div className="col-span-1 place-self-end text-xs sm:text-base">
                                {field.value}
                              </div>
                            </div>

                            <FormControl className="col-span-2">
                              <Slider
                                defaultValue={[
                                  annotation.getIn(['style', 'fontSize']),
                                ]}
                                max={50}
                                min={4}
                                step={1}
                                onValueChange={(values) =>
                                  field.onChange(values)
                                }
                                onValueCommit={(values) => {
                                  updateAnnotation(
                                    session,
                                    graphql,
                                    pathname,
                                    dispatch,
                                    router,
                                    annotationsRenderer,
                                    story,
                                    annotation,
                                    {
                                      style_font_size: values[0],
                                    },
                                    {
                                      shouldRefreshRouter: true,
                                    }
                                  );
                                }}
                              />
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={annotationStyleForm.control}
                      name="length"
                      defaultValue={annotation.getIn(['style', 'length'])}
                      render={({ field }) => {
                        return (
                          <FormItem className="grid grid-cols-2 items-center">
                            <div className="col-span-2 grid grid-cols-2 items-center">
                              <FormLabel className="col-span-1 text-xs sm:text-base">
                                Text length
                              </FormLabel>

                              <div className="col-span-1 place-self-end text-xs sm:text-base">
                                {field.value}
                              </div>
                            </div>

                            <FormControl className="col-span-2">
                              <Slider
                                defaultValue={[
                                  annotation.getIn(['style', 'length']),
                                ]}
                                max={200}
                                min={10}
                                step={1}
                                onValueChange={(values) =>
                                  field.onChange(values)
                                }
                                onValueCommit={(values) => {
                                  updateAnnotation(
                                    session,
                                    graphql,
                                    pathname,
                                    dispatch,
                                    router,
                                    annotationsRenderer,
                                    story,
                                    annotation,
                                    {
                                      style_length: values[0],
                                    },
                                    {
                                      shouldRefreshRouter: true,
                                    }
                                  );
                                }}
                              />
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />
                  </form>
                </div>

                <div className="col-span-5">
                  <form className="flex flex-col gap-8">
                    <FormField
                      control={annotationStyleForm.control}
                      name="colorfulBackground"
                      defaultValue={annotation.getIn([
                        'style',
                        'colorfulBackground',
                      ])}
                      render={({ field }) => {
                        return (
                          <FormItem className="grid grid-cols-2 items-center gap-2">
                            <FormLabel className="col-span-2 text-xs sm:text-base">
                              Colorful background
                            </FormLabel>

                            <FormControl className="col-span-2">
                              <Switch
                                className="!my-0"
                                checked={field.value}
                                onCheckedChange={(isChecked) => {
                                  field.onChange(isChecked);

                                  updateAnnotation(
                                    session,
                                    graphql,
                                    pathname,
                                    dispatch,
                                    router,
                                    annotationsRenderer,
                                    story,
                                    annotation,
                                    {
                                      style_colorful_background: isChecked,
                                    },
                                    {
                                      shouldRefreshRouter: false,
                                    }
                                  );
                                }}
                              />
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />

                    {(() => {
                      if (isNullOrFalse(colorfulBackgroundWatcher)) {
                        return null;
                      }

                      return (
                        <FormFieldColorPicker
                          form={annotationStyleForm}
                          defaultValue={annotation.getIn(['style', 'color'])}
                          fieldName="color"
                          labelText="Background color"
                          presetColors={COLORS}
                          onColorPicked={(color) => {
                            updateAnnotation(
                              session,
                              graphql,
                              pathname,
                              dispatch,
                              router,
                              annotationsRenderer,
                              story,
                              annotation,
                              {
                                style_color: color,
                              },
                              { shouldRefreshRouter: false }
                            );
                          }}
                        />
                      );
                    })()}

                    <FormFieldColorPicker
                      form={annotationStyleForm}
                      defaultValue={annotation.getIn(['style', 'textColor'])}
                      fieldName="textColor"
                      labelText="Text color"
                      presetColors={COLORS}
                      onColorPicked={(color) => {
                        updateAnnotation(
                          session,
                          graphql,
                          pathname,
                          dispatch,
                          router,
                          annotationsRenderer,
                          story,
                          annotation,
                          {
                            style_text_color: color,
                          },
                          {
                            shouldRefreshRouter: false,
                          }
                        );
                      }}
                    />

                    <FormField
                      control={annotationStyleForm.control}
                      name="paddingHorizontal"
                      defaultValue={annotation.getIn([
                        'style',
                        'padding',
                        'horizontal',
                      ])}
                      render={({ field }) => {
                        return (
                          <FormItem className="grid grid-cols-2 items-center">
                            <div className="col-span-2 grid grid-cols-2 items-center">
                              <FormLabel className="col-span-1 text-xs sm:text-base">
                                Horizontal padding
                              </FormLabel>

                              <div className="col-span-1 place-self-end text-xs sm:text-base">
                                {field.value}
                              </div>
                            </div>

                            <FormControl className="col-span-2">
                              <Slider
                                defaultValue={[
                                  annotation.getIn([
                                    'style',
                                    'padding',
                                    'horizontal',
                                  ]),
                                ]}
                                max={50}
                                min={0}
                                step={1}
                                onValueChange={(values) =>
                                  field.onChange(values)
                                }
                                onValueCommit={(values) => {
                                  updateAnnotation(
                                    session,
                                    graphql,
                                    pathname,
                                    dispatch,
                                    router,
                                    annotationsRenderer,
                                    story,
                                    annotation,
                                    {
                                      style_padding_horizontal: values[0],
                                    },
                                    {
                                      shouldRefreshRouter: true,
                                    }
                                  );
                                }}
                              />
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={annotationStyleForm.control}
                      name="paddingVertical"
                      defaultValue={annotation.getIn([
                        'style',
                        'padding',
                        'vertical',
                      ])}
                      render={({ field }) => {
                        return (
                          <FormItem className="grid grid-cols-2 items-center">
                            <div className="col-span-2 grid grid-cols-2 items-center">
                              <FormLabel className="col-span-1 text-xs sm:text-base">
                                Vertical padding
                              </FormLabel>

                              <div className="col-span-1 place-self-end text-xs sm:text-base">
                                {field.value}
                              </div>
                            </div>

                            <FormControl className="col-span-2">
                              <Slider
                                defaultValue={[
                                  annotation.getIn([
                                    'style',
                                    'padding',
                                    'vertical',
                                  ]),
                                ]}
                                max={50}
                                min={0}
                                step={1}
                                onValueChange={(values) =>
                                  field.onChange(values)
                                }
                                onValueCommit={(values) => {
                                  updateAnnotation(
                                    session,
                                    graphql,
                                    pathname,
                                    dispatch,
                                    router,
                                    annotationsRenderer,
                                    story,
                                    annotation,
                                    {
                                      style_padding_vertical: values[0],
                                    },
                                    {
                                      shouldRefreshRouter: true,
                                    }
                                  );
                                }}
                              />
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />
                  </form>
                </div>

                <IoIosArrowRoundBack
                  size="1.5rem"
                  onClick={() => setIsEditingColor(false)}
                  className="col-span-2 ml-auto cursor-pointer"
                />
              </div>
            </Form>
          );
        }

        return (
          <>
            {(() => {
              if (!inEditMode) {
                return null;
              }

              return (
                <div className="flex">
                  {(() => {
                    if (isNew(annotation)) {
                      return null;
                    }

                    return (
                      <button
                        className="w-fit text-gray-400 hover:text-red-700"
                        type="button"
                        onClick={deleteAnnotationHandler}
                      >
                        <TiDelete size="2rem" />
                      </button>
                    );
                  })()}

                  <FiMoreVertical
                    onClick={() => setIsEditingColor(true)}
                    className="ml-auto cursor-pointer"
                  />
                </div>
              );
            })()}

            <AnnotationDateAndValues
              session={session}
              dispatch={dispatch}
              graphql={graphql}
              router={router}
              annotationsRenderer={annotationsRenderer}
              setPreventPopoupHide={setPreventPopoupHide}
              annotation={annotation}
              setAnnotation={setAnnotation}
              story={story}
              inEditMode={inEditMode}
            />

            {/* User picture and username + title and commentary. */}
            {(() => {
              if (
                !inEditMode &&
                isBlank(annotationToRender.get('title')) &&
                isBlank(annotationToRender.get('summary'))
              ) {
                return null;
              }

              return (
                <div className="flex items-center gap-4">
                  <UserPictureAndUsername
                    user={annotationToRender.get('author')}
                    className="w-8 flex-col sm:w-auto sm:min-w-fit"
                    width={96}
                    height={96}
                    sizes="(min-width: 640px) 96px, 32px"
                  />

                  <div
                    className={clsx([
                      'speech-bubble flex max-h-fit flex-col gap-2 rounded border px-1 py-1 shadow sm:px-3 sm:py-2',
                      inEditMode && 'w-full sm:w-[80ch]',
                    ])}
                  >
                    <EditableField
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      className="font-semibold placeholder:font-light placeholder:italic"
                      isEditing={inEditMode}
                      fieldPlaceholder="Add a title"
                      originalValue={annotationToRender.get('title')}
                      onFocus={(_e, _value) => {
                        if (inEditMode && isFunction(setPreventPopoupHide)) {
                          setPreventPopoupHide(true);
                        }
                      }}
                      onBlur={makeOnBlurHandler('title')}
                      onChange={(e, value) =>
                        isFunction(onAnnotationFieldChange) &&
                        onAnnotationFieldChange('title', e, value)
                      }
                      value={undefined}
                    />

                    <EditableField
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      markdown
                      markdownMobileTiny
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      className="placeholder:font-light placeholder:italic"
                      isEditing={inEditMode}
                      element="textarea"
                      fieldPlaceholder="Add some commentary"
                      originalValue={annotationToRender.get('summary')}
                      onFocus={(_e, _value) => {
                        if (inEditMode && isFunction(setPreventPopoupHide)) {
                          setPreventPopoupHide(true);
                        }
                      }}
                      onBlur={makeOnBlurHandler('summary')}
                      onChange={(e, value) =>
                        isFunction(onAnnotationFieldChange) &&
                        onAnnotationFieldChange('summary', e, value)
                      }
                      value={undefined}
                    />

                    {(() => {
                      if (isBlank(annotationToRender.get('updatedAt'))) {
                        return null;
                      }

                      return (
                        <p className="mt-auto place-self-end self-end justify-self-end text-gray-400 sm:!text-sm">
                          Last updated{' '}
                          {formatRelativeFromNow(
                            annotationToRender.get('updatedAt')
                          )}
                        </p>
                      );
                    })()}
                  </div>
                </div>
              );
            })()}

            {/* Event. */}
            {(() => {
              if (isNew(annotation)) {
                return null;
              }

              if (
                inEditMode &&
                annotation.getIn(['source', 'type']) === 'self'
              ) {
                return (
                  <Form {...annotationUrlForm}>
                    <form
                      onSubmit={annotationUrlForm.handleSubmit(
                        addAnnotationEvent
                      )}
                      className="m-2"
                    >
                      <FormField
                        control={annotationUrlForm.control}
                        name="url"
                        render={({ field }) => (
                          <FormItem>
                            <Input
                              onFocus={(_e) => setPreventPopoupHide(true)}
                              className=""
                              placeholder="(Optional) add a URL and press enter to submit"
                              {...field}
                            />

                            <FormDescription>
                              This can be a tweet, a news article, a blog post,
                              or any website.
                            </FormDescription>

                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </form>
                  </Form>
                );
              }

              const annotationEventComponent = makeAnnotationEventComponent(
                story,
                annotation
              );

              if (!annotationEventComponent) {
                return null;
              }

              // NOTE: "-mx-2 -mb-2 p-2 sm:-mx-4 sm:-mb-4 sm:p-4" is synced to
              // the parent container's "p-4".
              return (
                <div className="break-nicely -mx-2 -mb-2 flex flex-col bg-gray-100 p-2 opacity-90 sm:-mx-4 sm:-mb-4 sm:p-4">
                  {(() => {
                    if (!inEditMode) {
                      return null;
                    }

                    return (
                      <button
                        className="mb-2 w-fit text-gray-400 hover:text-red-700"
                        type="button"
                        onClick={deleteAnnotationEventHandler}
                      >
                        <TiDelete size="1.5rem" />
                      </button>
                    );
                  })()}

                  <div className="">{annotationEventComponent}</div>
                </div>
              );
            })()}
          </>
        );
      })()}
    </div>
  );
}

export default Annotation;
